import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import axios from "axios";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { clearCart } from "../../lib/cart";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResult({ success = true }) {
  const [params] = useSearchParams();
  const txnid = params.get("txnid") || "";
  const [pay, setPay] = useState(null);

  useEffect(() => {
    if (!txnid) return;
    axios.get(`${API}/api/payments/${txnid}`).then((r) => {
      setPay(r.data);
      if (success && r.data?.items?.length) clearCart();
    }).catch(() => {});
  }, [txnid, success]);

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen flex flex-col" data-testid={success ? "payment-success" : "payment-failure"}>
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center px-5 py-24">
        <div className="w-full max-w-lg border border-[#C9A84C]/30 bg-[#0A1628] p-10 sm:p-12 text-center">
          <span className={`inline-flex items-center justify-center w-16 h-16 rounded-full border ${success ? "border-emerald-500/50 bg-emerald-500/10" : "border-red-500/50 bg-red-500/10"}`}>
            {success ? <Check size={30} className="text-emerald-400" /> : <X size={30} className="text-red-400" />}
          </span>
          <h1 className="font-marcellus text-3xl text-white mt-6">{success ? "Payment Successful" : "Payment Failed"}</h1>
          {success ? (
            <p className="text-sm text-slate-300 mt-4 leading-relaxed" data-testid="payment-success-message">
              Thank you for choosing NexSecure Technologies. Our team will contact you within 24 hours.
            </p>
          ) : (
            <p className="text-sm text-slate-300 mt-4 leading-relaxed">
              Your payment could not be completed. No amount has been charged by us — if your bank shows a debit, it is automatically reversed by the bank within a few working days.
            </p>
          )}
          {pay && (
            <div className="mt-8 text-left border border-white/10 divide-y divide-white/10" data-testid="payment-details">
              <div className="flex justify-between gap-6 px-5 py-3.5"><span className="text-xs tracking-[0.16em] uppercase text-slate-500 shrink-0">Transaction ID</span><span className="text-sm text-white font-medium text-right break-all" data-testid="payment-txn-id">{pay.mihpayid || pay.txnid}</span></div>
              <div className="flex justify-between gap-6 px-5 py-3.5"><span className="text-xs tracking-[0.16em] uppercase text-slate-500 shrink-0">Item</span><span className="text-sm text-white text-right">{pay.product}</span></div>
              {pay.items?.length > 0 && (
                <div className="px-5 py-3.5" data-testid="payment-order-summary">
                  <p className="text-xs tracking-[0.16em] uppercase text-slate-500 mb-3">Order Summary</p>
                  {pay.items.map((i) => (
                    <div key={i.product_id} className="flex justify-between gap-4 py-1 text-sm">
                      <span className="text-slate-300">{i.name} ×{i.quantity}</span>
                      <span className="text-white">₹{i.line_total.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  <div className="flex justify-between gap-4 py-1 text-sm mt-2 pt-2 border-t border-white/10"><span className="text-slate-500">Subtotal</span><span className="text-slate-300">₹{pay.subtotal?.toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between gap-4 py-1 text-sm"><span className="text-slate-500">GST (18%)</span><span className="text-slate-300">₹{pay.gst?.toLocaleString("en-IN")}</span></div>
                </div>
              )}
              <div className="flex justify-between gap-6 px-5 py-3.5"><span className="text-xs tracking-[0.16em] uppercase text-slate-500 shrink-0">Amount Paid</span><span className="text-lg font-bold text-[#C9A84C]" data-testid="payment-amount">₹{pay.amount}</span></div>
            </div>
          )}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {!success && (
              <Link to="/services" className="bg-[#C9A84C] hover:bg-[#dbb95c] text-[#0A1628] font-semibold text-sm px-8 py-3.5 transition-colors" data-testid="payment-try-again">
                Try Again
              </Link>
            )}
            <Link to="/" className="border border-[#C9A84C]/60 text-[#C9A84C] hover:bg-[#C9A84C]/10 font-semibold text-sm px-8 py-3.5 transition-colors" data-testid="payment-home">
              Back to Home
            </Link>
          </div>
          {success && <p className="text-[11px] text-slate-500 mt-6">A confirmation email has been sent to your email address. You can re-download this receipt anytime at <Link to="/receipts" className="text-nx-goldlight hover:text-white transition-colors" data-testid="payment-receipts-link">nexsecure.in/receipts</Link>.</p>}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
